import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { dateFormats } from 'commons/src/constants';
import { BuildingType } from 'commons/src/models/commonTypeScript';
import { IaqInsightData, PDFContext, ReportSensorThresholds } from '../../../../models/common';
import IaqInsight2BuildingDetails from './components/IaqInsight2BuildingDetails';
import IaqInsight2Footer from './components/IaqInsight2Footer';
import IaqInsight2Header from './components/IaqInsight2Header';
import IaqInsight2ReportDetails from './components/IaqInsight2ReportDetails';
import IaqInsight2SensorSection from './components/IaqInsight2SensorSection';
import IaqInsight2SummaryCards from './components/IaqInsight2SummaryCards';
import { SpaceMeasurementDataColumns } from './utils/pdfHelpers';
import iaqInsight2PdfStyles from './utils/pdfStyles';

type IaqInsight2PdfDocumentProps = {
    building: BuildingType;
    spaceMeasurementColumnsBySensorType: { [sensor: string]: SpaceMeasurementDataColumns };
    translationFunction: PDFContext;
    dateFormat: keyof typeof dateFormats;
    reports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
    reportDetails: {
        deviceTypes: string[];
        userName: string;
        reportId: string;
    };
    deviceTypesInReport: string[];
    comment: string;
    listOfImages: { data: string; serialNumber: string }[];
    fromDate: string | null;
    toDate: string | null;
    hideCharts?: boolean;
};

const IaqInsight2PdfDocument = React.memo(
    ({
        building,
        spaceMeasurementColumnsBySensorType,
        translationFunction,
        dateFormat,
        reports,
        thresholds,
        reportDetails,
        deviceTypesInReport,
        comment,
        listOfImages,
        fromDate,
        toDate,
        hideCharts,
    }: IaqInsight2PdfDocumentProps): React.ReactElement => {
        const sortedSensorTypeKeys = Object.keys(reports).sort((sensorTypeA, sensorTypeB) =>
            sensorTypeA.localeCompare(sensorTypeB)
        );

        return (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <Document>
                <Page size="A4" style={iaqInsight2PdfStyles.page}>
                    <IaqInsight2Header
                        building={building}
                        translationFunction={translationFunction}
                        fromDate={fromDate}
                        toDate={toDate}
                    />
                    <IaqInsight2BuildingDetails
                        translationFunction={translationFunction}
                        building={building}
                        dateFormat={dateFormat}
                    />
                    <Text style={iaqInsight2PdfStyles.sectionTitle}>
                        {translationFunction('IAQReport2.AirQualityOverview')}
                    </Text>
                    <Text style={iaqInsight2PdfStyles.airQualityAveragesDescription}>
                        {translationFunction('IAQReport2.AirQualityAveragesDescription')}
                    </Text>
                    <IaqInsight2SummaryCards
                        translationFunction={translationFunction}
                        reports={reports}
                        thresholds={thresholds}
                    />
                    {sortedSensorTypeKeys.map((sensorTypeKey, sensorTypeIndex) => {
                        const isCo2 = sensorTypeKey === 'co2';
                        const needsPageBreak = sensorTypeIndex > 0 || isCo2;

                        return (
                            <React.Fragment key={`sensor-${sensorTypeKey}`}>
                                {needsPageBreak && <View break />}
                                <IaqInsight2SensorSection
                                    sensorType={sensorTypeKey}
                                    translationFunction={translationFunction}
                                    sensorReportData={reports[sensorTypeKey]}
                                    sensorThresholdConfiguration={thresholds[sensorTypeKey]}
                                    spaceMeasurementColumns={spaceMeasurementColumnsBySensorType[sensorTypeKey]}
                                    listOfImages={listOfImages}
                                    hideCharts={hideCharts}
                                />
                            </React.Fragment>
                        );
                    })}
                    <IaqInsight2ReportDetails
                        userName={reportDetails.userName}
                        dateFormat={dateFormat}
                        translationFunction={translationFunction}
                        reportId={reportDetails.reportId}
                        deviceTypes={deviceTypesInReport}
                        comment={comment}
                    />
                    <IaqInsight2Footer />
                </Page>
            </Document>
        );
    }
);

export default IaqInsight2PdfDocument;
